import {
	Autocomplete,
	CircularProgress,
	FormControl,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import {useRefundRequest} from 'presentation/pages/private/refund/hooks';
import {translations} from 'presentation/translations-new';
import {formatText} from 'presentation/utils';
import React, {useEffect, useState} from 'react';
import {Controller, useFormContext} from 'react-hook-form';
import {useGetRefundProcedureTherapies} from '../../../../hooks/useGetRefundProcedureTherapies';
import type {TherapyDataSchema} from '../../schemas/therapyDataSchema';
import {RequestErrorModal} from 'presentation/pages/private/refund/components';
import {useUpdateBatchProcedureMutation} from '../../../../queries/useUpdateBatchProcedureMutation';

const {loading: loadingText} = translations['pt-br'].privateFlow.refund;

const {therapyType, selectTherapyType, therapyTypeNotFound} =
	translations['pt-br'].privateFlow.refund.therapies;

export const TherapyTypeField = () => {
	const {control, clearErrors, watch} = useFormContext<TherapyDataSchema>();
	const watchedSessions = watch('sessions');
	const isEmptySessions = watchedSessions.filter(session => session.isSaved).length <= 0;

	const {requests} = useRefundRequest();
	const {isErrorTherapyTypes, isLoadingTherapyTypes, refetchTherapyTypes, therapyTypesData} =
		useGetRefundProcedureTherapies();

	const {mutate: updateBatchProcedure, isPending: isLoadingBatchUpdate} =
		useUpdateBatchProcedureMutation();

	const [openTherapyTypeErrorModal, setOpenTherapyTypeErrorModal] = useState<boolean>(false);
	const [openBatchUpdateErrorModal, setOpenBatchUpdateErrorModal] = useState<boolean>(false);

	const handleTherapyTypeOpen = () => {
		if (!therapyTypesData) {
			refetchTherapyTypes();
		}
	};

	useEffect(() => {
		if (isErrorTherapyTypes) {
			setOpenTherapyTypeErrorModal(true);
		}
	}, [isErrorTherapyTypes]);

	return (
		<Stack gap={1}>
			<Typography color="primary" fontWeight={600}>
				{therapyType}
			</Typography>

			<Controller
				control={control}
				name="therapyType"
				render={({field: {value, onChange}, fieldState: {error}}) => (
					<FormControl>
						<Autocomplete
							id="therapy-type-autocomplete"
							options={therapyTypesData || []}
							getOptionLabel={option =>
								option?.description === 'RPG'
									? option.description
									: formatText(option?.description || '')
							}
							value={value}
							isOptionEqualToValue={(option, valueOption) =>
								option.code === valueOption.code
							}
							onChange={(_, newValue) => {
								if (
									!isEmptySessions &&
									value?.code &&
									newValue?.code &&
									value?.code !== newValue?.code &&
									requests.protocolCode &&
									requests.requestData?.[0]?.requestCode
								) {
									updateBatchProcedure(
										{
											procedureCodeCurrent: Number(value.code),
											procedureCodeNew: Number(newValue.code),
											protocolDetail: {
												protocol: requests?.protocol,
												protocolCode: requests?.protocolCode.toString(),
												request: requests.requestData?.[0]?.request,
												requestCode: String(
													requests.requestData?.[0]?.requestCode,
												),
											},
										},
										{
											onError: () => {
												onChange(value);
												setOpenBatchUpdateErrorModal(true);
											},
										},
									);
								}
								onChange(newValue);
								clearErrors('therapyType');
							}}
							onOpen={handleTherapyTypeOpen}
							loading={isLoadingTherapyTypes}
							loadingText={loadingText}
							noOptionsText={therapyTypeNotFound}
							disableClearable
							renderInput={params => (
								<TextField
									{...params}
									disabled={isLoadingBatchUpdate}
									placeholder={selectTherapyType}
									label={selectTherapyType}
									error={!!error}
									helperText={error?.message}
									slotProps={{
										input: {
											...params.InputProps,
											endAdornment: (
												<React.Fragment>
													{isLoadingTherapyTypes ||
													isLoadingBatchUpdate ? (
														<CircularProgress
															color="inherit"
															size={20}
														/>
													) : null}
													{params.InputProps.endAdornment}
												</React.Fragment>
											),
										},
									}}
								/>
							)}
						/>
					</FormControl>
				)}
			/>
			<RequestErrorModal
				open={openTherapyTypeErrorModal}
				setOpen={setOpenTherapyTypeErrorModal}
			/>
			<RequestErrorModal
				open={openBatchUpdateErrorModal}
				setOpen={setOpenBatchUpdateErrorModal}
			/>
		</Stack>
	);
};
